// Project tools — concrete ToolDefinition[] backed by a FileStore +
// scoped to a single project root.
//
// Design choices:
//
//   1. SCOPING — every tool prepends `projectRoot/` to the path the model
//      provides. The model talks in project-relative paths
//      ("index.html", "slides/01.html", "shared/tokens.css") and never
//      sees the parent OPFS namespace. splitPath() inside FileStore
//      throws on `..` so the model can't escape upward even if it tries.
//
//   2. STRINGIFIED RESULTS — ToolDefinition.execute returns a string
//      because the model only consumes strings via tool_result blocks.
//      For listings we serialize as text; for reads we return the raw
//      file content; for writes / edits we return a brief confirmation.
//
//   3. FAIL-SOFT ERRORS — when an operation fails (file missing, parent
//      directory absent, ambiguous edit match) we throw with a message
//      the model can read on its next turn and recover from. The agent
//      loop converts thrown errors into tool_result with isError: true,
//      which the provider passes back to the model.
//
//   4. NO BASH — by deliberate decision (Q-stage). Browser sandbox can't
//      shell out anyway, but we'd refuse even if it could.
//   5. NO IMPLICIT WEB FETCH — the agent can't reach external URLs from
//      tools alone. WebSearch as a tool gets added separately when we
//      know which provider's web-search beta we're tapping.

import type { FileStore } from '../filestore';
import type { ToolDefinition as AgentTool } from '../agent/types';

export interface BuildProjectToolsArgs {
  fileStore: FileStore;
  /**
   * Project root (relative to FileStore root). E.g. "untitled-2026-05-01-abc".
   * All tool inputs are interpreted relative to this.
   */
  projectRoot: string;
}

export function buildProjectTools(args: BuildProjectToolsArgs): AgentTool[] {
  const { fileStore, projectRoot } = args;
  const root = projectRoot.replace(/\/+$/, '');

  const fullPath = (relative: string): string => {
    // Reject empty / root-equivalent paths up front for clearer errors.
    const cleaned = relative.replace(/^\/+|\/+$/g, '');
    if (!cleaned) throw new Error('path cannot be empty or root');
    return `${root}/${cleaned}`;
  };

  return [readFile, writeFile, editFile, listDirectory].map((build) =>
    build({ fileStore, root, fullPath }),
  );
}

// ---- Individual tool builders --------------------------------------------

interface ToolBuildContext {
  fileStore: FileStore;
  root: string;
  fullPath: (relative: string) => string;
}

function readFile(ctx: ToolBuildContext): AgentTool {
  return {
    name: 'read_file',
    description:
      'Read a UTF-8 text file from the current project. Returns the file content as text. Use this BEFORE editing a file to see its current state.',
    inputSchema: {
      type: 'object',
      properties: {
        path: {
          type: 'string',
          description:
            'Path relative to the project root (e.g. "index.html" or "slides/02.html"). Must NOT start with "/" or contain "..".',
        },
      },
      required: ['path'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const path = ensureString(input, 'path');
      try {
        return await ctx.fileStore.readTextFile(ctx.fullPath(path));
      } catch (err) {
        throw new Error(
          `read_file failed: ${path} — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },
  };
}

function writeFile(ctx: ToolBuildContext): AgentTool {
  return {
    name: 'write_file',
    description:
      'Create-or-replace a UTF-8 text file in the current project. Parent directories are created as needed. WARNING: overwrites existing content silently — read_file first if uncertain.',
    inputSchema: {
      type: 'object',
      properties: {
        path: {
          type: 'string',
          description: 'Path relative to the project root.',
        },
        content: {
          type: 'string',
          description: 'Full file content. Existing content is replaced verbatim.',
        },
      },
      required: ['path', 'content'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const path = ensureString(input, 'path');
      const content = ensureString(input, 'content');
      try {
        await ctx.fileStore.writeTextFile(ctx.fullPath(path), content);
        return `Wrote ${content.length} chars → ${path}`;
      } catch (err) {
        throw new Error(
          `write_file failed: ${path} — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },
  };
}

function editFile(ctx: ToolBuildContext): AgentTool {
  return {
    name: 'edit_file',
    description:
      'Find-and-replace within an existing project file. By default, old_string must match EXACTLY ONCE (uniqueness check) — pass replace_all: true to replace every occurrence. Preserves the rest of the file verbatim. Useful for surgical tweaks without rewriting the whole file.',
    inputSchema: {
      type: 'object',
      properties: {
        path: {
          type: 'string',
          description: 'Path relative to the project root. Must already exist.',
        },
        old_string: {
          type: 'string',
          description:
            'Exact text to find. Include enough surrounding context to make the match unambiguous when replace_all is false.',
        },
        new_string: {
          type: 'string',
          description: 'Replacement text. Empty string deletes the match.',
        },
        replace_all: {
          type: 'boolean',
          description:
            'When true, replace every occurrence; when false (default), require exactly one match.',
        },
      },
      required: ['path', 'old_string', 'new_string'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const path = ensureString(input, 'path');
      const oldStr = ensureString(input, 'old_string');
      const newStr = ensureString(input, 'new_string');
      const replaceAll = Boolean(input.replace_all);
      if (oldStr === '') {
        throw new Error('edit_file: old_string cannot be empty');
      }
      let original: string;
      try {
        original = await ctx.fileStore.readTextFile(ctx.fullPath(path));
      } catch (err) {
        throw new Error(
          `edit_file failed (read): ${path} — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      const occurrences = countOccurrences(original, oldStr);
      if (replaceAll) {
        if (occurrences === 0) {
          throw new Error(
            `edit_file: old_string not found in ${path}. Read the file first to confirm exact text.`,
          );
        }
        // split/join replace-all sidesteps the need to regex-escape the needle.
        const updated = original.split(oldStr).join(newStr);
        await ctx.fileStore.writeTextFile(ctx.fullPath(path), updated);
        return `Replaced ${occurrences} occurrence(s) of old_string → ${path}`;
      }
      if (occurrences === 0) {
        throw new Error(
          `edit_file: old_string not found in ${path}. Read the file first to confirm exact text.`,
        );
      }
      if (occurrences > 1) {
        throw new Error(
          `edit_file: old_string matches ${occurrences} times in ${path}. Add more surrounding context to make it unique, OR pass replace_all: true.`,
        );
      }
      const updated = original.replace(oldStr, newStr);
      await ctx.fileStore.writeTextFile(ctx.fullPath(path), updated);
      return `Edited ${path} (1 replacement)`;
    },
  };
}

function listDirectory(ctx: ToolBuildContext): AgentTool {
  return {
    name: 'list_directory',
    description:
      'List files and subdirectories under a directory in the current project. Returns one entry per line in the form "<kind>  <size?>  <name>". Use path "." or "" for the project root.',
    inputSchema: {
      type: 'object',
      properties: {
        path: {
          type: 'string',
          description:
            'Directory path relative to the project root. Use "." (or empty) for the project root itself.',
        },
      },
      required: ['path'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const rel = String(input.path ?? '').trim();
      const target = rel === '' || rel === '.' ? ctx.root : ctx.fullPath(rel);
      const entries = await ctx.fileStore.listDir(target);
      if (entries.length === 0) {
        return '(empty directory)';
      }
      const lines = entries.map((e) => {
        if (e.kind === 'directory') return `dir   —      ${e.name}/`;
        const size = e.size != null ? formatBytes(e.size) : '';
        return `file  ${size.padStart(6, ' ')}  ${e.name}`;
      });
      return lines.join('\n');
    },
  };
}

// ---- Small validation + formatting helpers -------------------------------

function ensureString(input: Record<string, unknown>, key: string): string {
  const v = input[key];
  if (typeof v !== 'string') {
    throw new Error(`tool input '${key}' must be a string (got ${typeof v})`);
  }
  return v;
}

function countOccurrences(haystack: string, needle: string): number {
  if (needle === '') return 0;
  let count = 0;
  let pos = 0;
  while (true) {
    const idx = haystack.indexOf(needle, pos);
    if (idx === -1) break;
    count++;
    pos = idx + needle.length;
  }
  return count;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`;
  return `${(n / (1024 * 1024)).toFixed(1)}M`;
}

// Re-export the agent's ToolDefinition type so callers can ask for the
// exact thing they're getting back.
export type { ToolDefinition } from '../agent/types';
